import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Copy, Check, KeyRound } from "lucide-react";
import { api, withReason } from "@/lib/api";

const PLANS = ["pilot", "growth", "enterprise"];
const ROLES = ["owner", "admin", "operator", "viewer"];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const blankMember = () => ({ email: "", name: "", role: "owner" });
const blankConnector = () => ({
  name: "Support mailbox", type: "imap",
  host: "", port: 993, folder: "INBOX",
  smtp_host: "", smtp_port: 587, from_address: "",
  username: "", password: "", live: false,
});

/**
 * What used to be scripts/connect_mailbox.sh plus a page of hand-written SQL.
 *
 * The whole thing is one POST and one transaction, so a failure leaves nothing
 * behind and the form can simply be corrected and resubmitted. The response
 * carries generated passwords and the service-account key, which exist exactly
 * once — hence the handover panel rather than a redirect on success.
 */
export default function PlatformOnboarding() {
  const [form, setForm] = useState({
    name: "", slug: "", slugTouched: false,
    environment: "demo", plan_code: "pilot", pair_key: "",
    service_account_name: "Helpdesk push", reason: "",
  });
  const [members, setMembers] = useState([blankMember()]);
  const [connectors, setConnectors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");
  const nav = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onName = (v) =>
    setForm((f) => ({ ...f, name: v, slug: f.slugTouched ? f.slug : slugify(v) }));

  const setMember = (i, k, v) =>
    setMembers((m) => m.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const setConnector = (i, k, v) =>
    setConnectors((c) => c.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const copy = (label, value) => {
    navigator.clipboard?.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        name: form.name,
        slug: form.slug,
        environment: form.environment,
        plan_code: form.plan_code,
        pair_key: form.pair_key || null,
        members: members
          .filter((m) => m.email.trim())
          .map((m) => ({ email: m.email.trim(), name: m.name.trim() || m.email.trim(), role: m.role })),
        connectors: connectors.map((c) => ({
          name: c.name,
          type: c.type,
          // Unlike the old Connectors form, SMTP and the sending address are
          // set here — without them the connector can receive but never reply.
          config: {
            host: c.host, port: Number(c.port) || 993, folder: c.folder || "INBOX",
            ...(c.smtp_host ? { smtp_host: c.smtp_host, smtp_port: Number(c.smtp_port) || 587 } : {}),
            ...(c.from_address ? { from_address: c.from_address } : {}),
          },
          credentials: c.username || c.password ? { username: c.username, password: c.password } : null,
          live: c.live,
        })),
        service_account_name: form.service_account_name || null,
      };
      const { data } = await api.post("/platform/onboarding", payload, withReason(form.reason));
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Onboarding failed");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="px-4 py-5 md:px-8 md:py-6" data-testid="onboarding-result">
        <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>
          Platform
        </div>
        <h1 className="font-display text-[26px]">{form.name} is live</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
          These credentials are shown once and cannot be retrieved again. Hand them over before leaving this page.
        </p>

        <div className="card p-5 mt-5">
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
            Sign-in credentials
          </div>
          {result.credentials?.map((c) => (
            <div key={c.email} className="flex items-center justify-between py-2"
                 style={{ borderBottom: "1px solid var(--border)" }}
                 data-testid={`credential-${c.email}`}>
              <div>
                <div className="text-[13px]">{c.email}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.role}</div>
              </div>
              {c.password ? (
                <button className="btn btn-ghost font-mono text-[12px]" onClick={() => copy(c.email, c.password)}>
                  {copied === c.email ? <Check size={12} /> : <Copy size={12} />} {c.password}
                </button>
              ) : (
                // Honest rather than convenient: this person already had a
                // login, so there is no new password to hand over.
                <span className="badge b-muted" title="Existing user — they keep the password they already use">
                  existing account
                </span>
              )}
            </div>
          ))}
        </div>

        {result.service_account_key && (
          <div className="card p-5 mt-4">
            <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
              <KeyRound size={11} className="inline mr-1" /> Service-account key
            </div>
            <p className="text-[12px] mb-3" style={{ color: "var(--text-secondary)" }}>
              For the client's own systems to push tickets to{" "}
              <span className="font-mono">POST /api/v1/ingest</span> with an{" "}
              <span className="font-mono">X-Api-Key</span> header. No database credentials needed.
            </p>
            <button className="btn btn-ghost font-mono text-[12px] break-all text-left"
                    data-testid="copy-service-key"
                    onClick={() => copy("key", result.service_account_key)}>
              {copied === "key" ? <Check size={12} /> : <Copy size={12} />} {result.service_account_key}
            </button>
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button className="btn" data-testid="goto-tenant"
                  onClick={() => nav(`/admin/tenants/${result.tenant_id}`)}>
            Open workspace
          </button>
          <button className="btn btn-ghost" onClick={() => { setResult(null); setCopied(""); }}>
            Onboard another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>
        Platform
      </div>
      <h1 className="font-display text-[26px]">Onboard a client</h1>
      <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
        Workspace, people, plan and starter connectors in one step. Nothing is created unless all of it succeeds.
      </p>

      <form onSubmit={submit} data-testid="onboarding-form">
        <div className="card p-5 mt-5">
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
            Workspace
          </div>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-5">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>Company name</label>
              <input className="input mt-1" data-testid="onboarding-name" value={form.name}
                     onChange={(e) => onName(e.target.value)} placeholder="Acme Freight" required />
            </div>
            <div className="col-span-12 md:col-span-3">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>Slug</label>
              <input className="input mt-1 font-mono" data-testid="onboarding-slug" value={form.slug}
                     onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value), slugTouched: true }))}
                     placeholder="acme-freight" required />
            </div>
            <div className="col-span-6 md:col-span-2">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>Environment</label>
              <select className="input mt-1" data-testid="onboarding-environment" value={form.environment}
                      onChange={(e) => set("environment", e.target.value)}>
                <option value="demo">demo</option>
                <option value="live">live</option>
              </select>
            </div>
            <div className="col-span-6 md:col-span-2">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>Plan</label>
              <select className="input mt-1" data-testid="onboarding-plan" value={form.plan_code}
                      onChange={(e) => set("plan_code", e.target.value)}>
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          {form.environment === "live" && (
            // Not a warning about this form. It is a warning about what the
            // workspace can do once it exists.
            <div className="badge b-sla mt-3 inline-block">
              A live workspace can reach real customers: connectors send real email.
            </div>
          )}
        </div>

        <div className="card p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              People
            </div>
            <button type="button" className="btn btn-ghost" data-testid="add-member"
                    onClick={() => setMembers((m) => [...m, blankMember()])}>
              <Plus size={12} /> Add person
            </button>
          </div>
          {members.map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`member-row-${i}`}>
              <input className="input col-span-12 md:col-span-5" placeholder="email@company.com" type="email"
                     value={m.email} onChange={(e) => setMember(i, "email", e.target.value)} />
              <input className="input col-span-12 md:col-span-4" placeholder="Full name"
                     value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} />
              <select className="input col-span-6 md:col-span-2" value={m.role}
                      onChange={(e) => setMember(i, "role", e.target.value)}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button type="button" className="btn btn-ghost col-span-6 md:col-span-1"
                      onClick={() => setMembers((rows) => rows.filter((_, idx) => idx !== i))}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <p className="text-[12px] mt-2" style={{ color: "var(--text-muted)" }}>
            Passwords are generated and shown once. Exactly one owner is required — nothing in the
            client's own UI can grant that role later.
          </p>
        </div>

        <div className="card p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Starter connectors
            </div>
            <button type="button" className="btn btn-ghost" data-testid="add-connector"
                    onClick={() => setConnectors((c) => [...c, blankConnector()])}>
              <Plus size={12} /> Add mailbox
            </button>
          </div>
          {connectors.length === 0 && (
            <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>
              Optional. The client can add these themselves later.
            </p>
          )}
          {connectors.map((c, i) => (
            <div key={i} className="mb-4 pb-4" data-testid={`connector-row-${i}`}
                 style={{ borderBottom: i < connectors.length - 1 ? "1px solid var(--border)" : "none" }}>
              <div className="grid grid-cols-12 gap-2">
                <input className="input col-span-12 md:col-span-4" placeholder="Name, e.g. Support mailbox"
                       value={c.name} onChange={(e) => setConnector(i, "name", e.target.value)} />
                <input className="input col-span-12 md:col-span-5" placeholder="IMAP host, e.g. imap.gmail.com"
                       value={c.host} onChange={(e) => setConnector(i, "host", e.target.value)} />
                <input className="input col-span-6 md:col-span-2" placeholder="Port" type="number"
                       value={c.port} onChange={(e) => setConnector(i, "port", e.target.value)} />
                <button type="button" className="btn btn-ghost col-span-6 md:col-span-1"
                        onClick={() => setConnectors((rows) => rows.filter((_, idx) => idx !== i))}>
                  <Trash2 size={12} />
                </button>
                <input className="input col-span-12 md:col-span-4" placeholder="Mailbox user"
                       value={c.username} onChange={(e) => setConnector(i, "username", e.target.value)} />
                <input className="input col-span-12 md:col-span-4" placeholder="Password / app password" type="password"
                       value={c.password} onChange={(e) => setConnector(i, "password", e.target.value)} />
                <input className="input col-span-12 md:col-span-3" placeholder="Folder"
                       value={c.folder} onChange={(e) => setConnector(i, "folder", e.target.value)} />
                <input className="input col-span-12 md:col-span-5" placeholder="SMTP host, e.g. smtp.gmail.com"
                       value={c.smtp_host} onChange={(e) => setConnector(i, "smtp_host", e.target.value)} />
                <input className="input col-span-6 md:col-span-2" placeholder="SMTP port" type="number"
                       value={c.smtp_port} onChange={(e) => setConnector(i, "smtp_port", e.target.value)} />
                <input className="input col-span-12 md:col-span-5" placeholder="Reply-from address"
                       value={c.from_address} onChange={(e) => setConnector(i, "from_address", e.target.value)} />
              </div>
              {!c.smtp_host && (
                <div className="badge b-sla mt-2 inline-block">
                  Without an SMTP host this mailbox can receive but never reply.
                </div>
              )}
              <label className="flex items-center gap-2 text-[12.5px] mt-2" style={{ color: "var(--text-secondary)" }}>
                <input type="checkbox" checked={c.live}
                       onChange={(e) => setConnector(i, "live", e.target.checked)} />
                Connect to the real mailbox (live workspaces only)
              </label>
            </div>
          ))}
        </div>

        <div className="card p-5 mt-4">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-5">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                Service-account name
              </label>
              <input className="input mt-1" data-testid="onboarding-service-account"
                     value={form.service_account_name}
                     onChange={(e) => set("service_account_name", e.target.value)}
                     placeholder="Leave blank to skip" />
              <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                Lets the client's own ticket system push to Prioris without sharing database credentials.
              </p>
            </div>
            <div className="col-span-12 md:col-span-7">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                Reason (recorded in the staff audit trail)
              </label>
              <input className="input mt-1" data-testid="onboarding-reason" value={form.reason}
                     onChange={(e) => set("reason", e.target.value)}
                     placeholder="e.g. signed pilot agreement 2026-09-11" />
            </div>
          </div>
        </div>

        {error && <div className="badge b-rev mt-4 inline-block" data-testid="onboarding-error">{error}</div>}

        <div className="flex gap-2 mt-5">
          <button className="btn" type="submit" disabled={busy} data-testid="submit-onboarding">
            {busy ? "Creating…" : "Create workspace"}
          </button>
          <button className="btn btn-ghost" type="button" onClick={() => nav("/admin")}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
